import {
  Form,
  Modal,
  Upload,
  Typography,
  Space,
  Input,
  Tooltip,
  Button,
  App
} from 'antd';
import { useModalStateContext, useUIDispatchContext } from 'providers/UI';
import { FC, memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFileUpload } from 'hooks';
import {
  InboxOutlined,
  QuestionCircleOutlined,
  SendOutlined
} from '@ant-design/icons';
import { useAppointmentDetails } from 'providers/AppointmentDetails';
import { createReportFile } from 'services/api/reports';

import { showRestError } from 'utils/showErrorMessage';

const { Text } = Typography;
const { TextArea } = Input;

const AddReportModal: FC = () => {
  const { t } = useTranslation();
  const { toggleModal } = useUIDispatchContext();
  const { appointmentReport } = useModalStateContext();
  const { refresh } = useAppointmentDetails();
  const [form] = Form.useForm();
  const { modal } = App.useApp();
  const [loading, setLoading] = useState(false);
  const {
    result: fileValue,
    setResult,
    upload
  } = useFileUpload({
    handleResult: true
  });

  const closeModal = useCallback(() => {
    toggleModal('appointmentReport', false);
    form.resetFields();
    setResult('');
  }, [form, setResult, toggleModal]);

  const appointmentId = appointmentReport.params?.id;

  const onFinish = (values: { notes: string }) => {
    if (!fileValue) {
      return modal.error({
        title: t('messages.error'),
        content: t('messages.please_upload_file', {
          button: t('reports.send_report')
        })
      });
    }

    setLoading(true);
    createReportFile({
      appointmentId: appointmentId as string,
      file: fileValue,
      note: values.notes
    })
      .then(() => {
        closeModal();
        refresh();
      })
      .catch(err => showRestError(err))
      .finally(() => setLoading(false));
  };

  return (
    <Modal
      open={appointmentReport.visible}
      title={t('reports.upload_report')}
      onCancel={closeModal}
      onOk={form.submit}
      closable={!loading}
      footer={[
        <Button key="back" onClick={closeModal} disabled={loading}>
          {t('common.cancel')}
        </Button>,
        <Button
          type="primary"
          key="submit"
          onClick={() => form.submit()}
          loading={loading}
        >
          <SendOutlined />
          {t('reports.send_report')}
        </Button>
      ]}
    >
      <Form form={form} onFinish={onFinish} layout="vertical">
        <Form.Item
          name="notes"
          label={
            <Space size={8}>
              <Text style={{ fontWeight: 'bold' }}>{t('reports.note')}</Text>
              <Tooltip placement="bottom" title={t('reports.tooltip_text')}>
                <QuestionCircleOutlined
                  style={{ color: '#FF7F4E', fontSize: 16, cursor: 'pointer' }}
                />
              </Tooltip>
            </Space>
          }
        >
          <TextArea
            placeholder={t('reports.note')}
            style={{ minHeight: 120 }}
          />
        </Form.Item>
        <Space style={{ width: '100%' }} direction="vertical">
          <Text style={{ fontWeight: 'bold' }}>
            {t('appointments_details.upload_file')}
          </Text>
          <Upload.Dragger
            name="file"
            accept="image/png, image/jpeg, .pdf"
            customRequest={e =>
              upload(e.file as File)
                .then(e.onSuccess)
                .catch(e.onError)
            }
            maxCount={1}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{t('reports.dragger_title')}</p>
            <p className="ant-upload-hint">{t('reports.dragger_text')}</p>
          </Upload.Dragger>
        </Space>
      </Form>
    </Modal>
  );
};

export default memo(AddReportModal);
